// MemberDetailsView.jsx
import { ArrowLeft, Edit2, Save, X, Building2, Globe, Phone, Mail, MapPin, Calendar, DollarSign, Users, Award } from "lucide-react";
import { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import RegionProvinceDistrictFields from "../../common/RegionSelectFields"; 
import { companySizes, industries, periodicities } from "../../../constants/options";
import Swal from "sweetalert2";
import { updateByAdmin } from "../../../services/companiesService";
import { useAuth } from "../../../context/authContext";
import ImageUploaderWithCrop from "../../images/ImageUploaderWithCrop";

const MemberDetailsView = ({ company, onBack, regionsData, onSave, isSubmitting }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const { user } = useAuth();
  if (!company) return null;

  // Clases de estilo mejoradas
  const inputClass = "w-full border-0 bg-gray-50 p-4 rounded-xl focus:ring-3 focus:ring-indigo-200 focus:bg-white transition-all duration-300 outline-none shadow-sm hover:shadow-md text-gray-800 placeholder-gray-400";
  const selectClass = "w-full border-0 bg-gray-50 p-4 rounded-xl focus:ring-3 focus:ring-indigo-200 focus:bg-white transition-all duration-300 outline-none appearance-none shadow-sm hover:shadow-md text-gray-800";
  const labelClass = "block mb-3 font-semibold text-gray-700 text-sm uppercase tracking-wide";
  const errorClass = "text-sm text-red-500 mt-2 font-medium bg-red-50 p-2 rounded-lg border-l-4 border-red-400";
  const helperTextClass = "text-xs text-gray-500 mt-2 bg-blue-50 p-2 rounded-lg";
  const sectionClass = "p-8 bg-white rounded-2xl shadow-lg border-0 transition-all duration-300 hover:shadow-xl relative overflow-hidden";

  // Estilos para pasar al componente de regiones
  const regionStyles = {
    selectClass,
    labelClass,
    errorClass,
    helperTextClass
  };

  // Schema de validación con Yup
  const validationSchema = Yup.object({
    business_name: Yup.string().required("El nombre comercial es obligatorio"),
    trade_name: Yup.string().required("El nombre de fantasía es obligatorio"),
    rut: Yup.string().required("El RUT es obligatorio"),
    industry: Yup.string().required("La industria es obligatoria"),
    contact_email: Yup.string().email("Correo electrónico inválido").required("El correo es obligatorio"),
    landline_phone: Yup.string().required("El teléfono fijo es obligatorio"),
    mobile_phone: Yup.string().required("El teléfono móvil es obligatorio"),
    address: Yup.string().required("La dirección es obligatoria"),
    region: Yup.string().required("La región es obligatoria"),
    province: Yup.string().required("La provincia es obligatoria"),
    district: Yup.string().required("La comuna es obligatoria"),  
    periodicity: Yup.string().required("La periodicidad es obligatoria"),
    amount: Yup.string()
    .required('El monto de la cuota es obligatorio')
    .test('decimal-check', 'Debe ser un número válido con punto (.) como separador y hasta 2 decimales', value => {
      if (!value) return false;
      if (value.includes(',')) return false;
      return /^\d+(\.\d{1,2})?$/.test(value);
    }),  
    company_size: Yup.string().required("El tamaño de empresa es obligatorio"),
    entry_date: Yup.string()
      .required('La fecha es obligatoria')
      .matches(
        /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[012])-\d{4}$/,
        'La fecha debe tener formato dd-mm-yyyy'
      ),
    description: Yup.string().required("La descripción es obligatoria"),
    status: Yup.string().required("El estado es obligatorio"),
    role: Yup.string().required("El rol es obligatorio"),
    web_page: Yup.string().url("URL inválida").required("La página web es obligatoria"),
    latitude: Yup.number()
      .typeError('Debe ser un número')
      .min(-90, 'Mínimo -90')
      .max(90, 'Máximo 90')
      .nullable()
      .transform((value, originalValue) => originalValue === '' ? null : value),
    longitude: Yup.number()
      .typeError('Debe ser un número')
      .min(-180, 'Mínimo -180')
      .max(180, 'Máximo 180')
      .nullable()
      .transform((value, originalValue) => originalValue === '' ? null : value),
  });

  const handleSaveChanges = async (values) => {
    try {
            const confirm = await Swal.fire({
              title: '¿Estás seguro de guardar los cambios?',
              text: "Los cambios no se pueden deshacer.",
              icon: 'warning',
              showCancelButton: true,
            })
    
            if (!confirm.isConfirmed) return;
            isSubmitting(true);
            const formData = new FormData();
            Object.keys(values).forEach(key => {
              if (['role', 'password', 'logo'].includes(key)) return;
              if (key === 'latitude' || key === 'longitude') {
                if (values[key] != null && values[key] !== '') {
                  formData.append(key, values[key]);
                }
                return;
              }
              formData.append(key, values[key]);
            });
            if (values.logo) {
              formData.append('logo', values.logo);
            }

            formData.delete('role');
            formData.delete('password');
            const response = await updateByAdmin(formData, values.rut);
            isSubmitting(false);
            if (response.success){
              Swal.fire({
                title: 'Éxito',
                text: 'Compañía guardada exitosamente',
                icon: 'success',
              }).then(() => {
                onSave();
                handleClose();
              })
              setIsEditing(false);
            }

            if (!response.success){
              await Swal.fire({
                title: 'Error',
                text: response.message,
                icon: 'error',
              });
            }
            
          } catch (error) {
            Swal.fire({
              title: 'Error',
              text: 'No se pudo guardar la compañía. Intenta nuevamente.',
              icon: 'error',
            })
          } finally {
            isSubmitting(false); 
          }
    }
  
  const handleClose = () => {
    setIsEditing(false);
    setImagePreview(null);
  }

  // Iconos para campos
  const getFieldIcon = (fieldName) => {
    const icons = {
      business_name: <Building2 className="w-4 h-4 text-indigo-500" />,
      trade_name: <Award className="w-4 h-4 text-purple-500" />,
      contact_email: <Mail className="w-4 h-4 text-blue-500" />,
      landline_phone: <Phone className="w-4 h-4 text-green-500" />,
      mobile_phone: <Phone className="w-4 h-4 text-emerald-500" />,
      address: <MapPin className="w-4 h-4 text-red-500" />,
      entry_date: <Calendar className="w-4 h-4 text-orange-500" />,
      amount: <DollarSign className="w-4 h-4 text-yellow-500" />,
      company_size: <Users className="w-4 h-4 text-teal-500" />,
      web_page: <Globe className="w-4 h-4 text-cyan-500" />,
      latitude: <MapPin className="w-4 h-4 text-teal-500" />,
      longitude: <MapPin className="w-4 h-4 text-teal-600" />,
    };
    return icons[fieldName] || <Building2 className="w-4 h-4 text-gray-500" />;
  };

  // Componente para un campo de visualización
  const ViewField = ({ label, value, icon, colorClass = "text-gray-800" }) => (
    <div className="group p-4 rounded-xl bg-gradient-to-r from-gray-50 to-white border border-gray-100 hover:shadow-md transition-all duration-300">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">{label}</p>
      </div>
      <p className={`text-base font-medium ${colorClass} leading-relaxed`}>{value || "-"}</p>
    </div>
  );



  const statusMap = {
    Active: "Activo",
    Inactive: "Inactivo",
    Merged: "Fusionado",
  };
  
  const companySizeMap = {
    Micro: "Micro",
    Small: "Pequeña",
    Medium: "Mediana",
    Large: "Grande",
  };

  // Función para obtener color del estado
  const getStatusColor = (status) => {
    const colors = {
      Active: "text-green-600",
      Inactive: "text-red-600",
      Merged: "text-blue-600"
    };
    return colors[status] || "text-gray-600";
  };

  // Componente para un campo editable con validación
  const EditField = ({ label, name, type = "text", helperText, disabled, icon }) => (
    <div className="mb-6 group">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <label htmlFor={name} className={labelClass}>
          {label} <span className="text-red-500">*</span>
        </label>
      </div>
      <Field
        type={type}
        id={name}
        name={name}
        className={`${inputClass} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        disabled={disabled}
      />
      {helperText && <p className={helperTextClass}>{helperText}</p>}
      <ErrorMessage
        name={name}
        component="div"
        className={errorClass}
      />
    </div>
  );

  // Componente para un área de texto editable con validación
  const EditTextareaField = ({ label, name, helperText }) => (
    <div className="mb-6 group">
      <label htmlFor={name} className={labelClass}>
        {label} <span className="text-red-500">*</span>
      </label>
      <Field
        as="textarea"
        id={name}
        name={name}
        rows="4"
        className={`${inputClass} resize-none`}
      />
      {helperText && <p className={helperTextClass}>{helperText}</p>}
      <ErrorMessage
        name={name}
        component="div"
        className={errorClass}
      />
    </div>
  );

  const EditSelectField = ({ label, name, options, helperText, icon, disabled }) => (
    <div className="mb-6 group">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <label htmlFor={name} className={labelClass}>
          {label} <span className="text-red-500">*</span>
        </label>
      </div>
      <Field
        as="select"
        id={name}
        name={name}
        className={selectClass}
        disabled={disabled}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Field>
      {helperText && <p className={helperTextClass}>{helperText}</p>}
      <ErrorMessage
        name={name}
        component="div"
        className={errorClass}
      />
    </div>
  );

    const companyStatus = company.status; // o donde tengas el status actual
    const isMerged = companyStatus === "Merged";

  const statusCompanies = [
    { label: "Activo", value: "Active" },
    { label: "Inactivo", value: "Inactive" },
    { label: "Fusionado", value: "Merged" },
  ];

  // Eliminar "Fusionado" si el estado es Active o Inactive
  const modifiedCompanyStatus =
    company.status === "Active" || company.status === "Inactive"
      ? statusCompanies.filter((s) => s.value !== "Merged")
      : statusCompanies;

  

  return (
    <div className="w-full min-h-screen bg-gray-50">
      {/* Header con gradiente */}
        <div className="p-8">
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={onBack}
              aria-label="Volver atrás"
              className="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-[#DC3465] backdrop-blur-sm rounded-xl shadow-lg hover:bg-[#c02e59] transition-all duration-300 border border-white/20"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Volver Atrás
            </button>
            
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center px-6 py-3 bg-blue-600 backdrop-blur-sm text-white rounded-xl hover:bg-blue-700 transition-all duration-300 shadow-lg border border-white/20"
              >
                <Edit2 className="w-5 h-5 mr-2" />
                Editar
              </button>
            ) : null}
          </div>

          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <Building2 className="w-8 h-8 text-black" />
            </div>
            <div>
              <h1 className="text-3xl font-bold mb-2">Detalles del Socio</h1>
              <p className="text-black text-lg">{company.business_name}</p>
            </div>
          </div>
        </div>

      {/* Contenido principal */}
      <div className="p-8">
        {!isEditing ? (
          <div className={sectionClass}>
            {/* Logo de empresa */}
            <div className="flex flex-col lg:flex-row gap-8 mb-8">
              <div className="flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ViewField 
                    label="Razón Social" 
                    value={company.business_name} 
                    icon={getFieldIcon('business_name')}
                  />
                  <ViewField 
                    label="Nombre de fantasía" 
                    value={company.trade_name} 
                    icon={getFieldIcon('trade_name')}
                  />
                  <ViewField 
                    label="RUT" 
                    value={company.rut} 
                    icon={<Building2 className="w-4 h-4 text-gray-500" />}
                  />
                  <ViewField 
                    label="Industria" 
                    value={company.industry} 
                    icon={<Users className="w-4 h-4 text-indigo-500" />}
                  />
                  <ViewField 
                    label="Correo de contacto" 
                    value={company.contact_email} 
                    icon={getFieldIcon('contact_email')}
                  />
                  <ViewField 
                    label="Teléfono fijo" 
                    value={company.landline_phone} 
                    icon={getFieldIcon('landline_phone')}
                  />
                  <ViewField 
                    label="Teléfono móvil" 
                    value={company.mobile_phone} 
                    icon={getFieldIcon('mobile_phone')}
                  />
                  <ViewField 
                    label="Dirección" 
                    value={company.address} 
                    icon={getFieldIcon('address')}
                  />
                  <ViewField 
                    label="Región" 
                    value={company.region} 
                    icon={<MapPin className="w-4 h-4 text-blue-500" />}
                  />
                  <ViewField 
                    label="Provincia" 
                    value={company.province} 
                    icon={<MapPin className="w-4 h-4 text-blue-400" />}
                  />
                  <ViewField 
                    label="Comuna" 
                    value={company.district} 
                    icon={<MapPin className="w-4 h-4 text-blue-300" />}
                  />
                  <ViewField 
                    label="Latitud" 
                    value={company.latitude} 
                    icon={getFieldIcon('latitude')}
                  />
                  <ViewField 
                    label="Longitud" 
                    value={company.longitude} 
                    icon={getFieldIcon('longitude')}
                  />
                  <ViewField 
                    label="Periodicidad" 
                    value={company.periodicity} 
                    icon={<Calendar className="w-4 h-4 text-purple-500" />}
                  />
                  <ViewField 
                    label="Monto cuota" 
                    value={company.amount} 
                    icon={getFieldIcon('amount')}
                  />
                  <ViewField 
                    label="Tamaño empresa" 
                    value={companySizeMap[company.company_size] || company.company_size} 
                    icon={getFieldIcon('company_size')}
                  />
                  <ViewField 
                    label="Fecha de ingreso" 
                    value={company.entry_date} 
                    icon={getFieldIcon('entry_date')}
                  />
                  <ViewField 
                    label="Estado" 
                    value={statusMap[company.status] || company.status} 
                    icon={<Award className="w-4 h-4 text-yellow-500" />}
                    colorClass={getStatusColor(company.status)}
                  />
                  <ViewField 
                    label="Rol" 
                    value={company.role} 
                    icon={<Users className="w-4 h-4 text-pink-500" />}
                  />
                  <ViewField 
                    label="Web" 
                    value={company.web_page} 
                    icon={getFieldIcon('web_page')}
                  />
                </div>
              </div>
              
              {/* Logo */}
              <div className="flex flex-col items-center">
                <div className="w-48 h-48 rounded-3xl overflow-hidden border-4 border-white shadow-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-4">
                  {company.logo ? (
                    <img
                      src={company.logo}
                      alt="Logo de la empresa"
                      className="object-containt w-full h-full"
                    />
                  ) : (
                    <div className="text-center">
                      <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-400 text-sm">Sin logo</p>
                    </div>
                  )}
                </div>
                <p className="text-sm font-medium text-gray-600 mt-4">Logo de la empresa</p>
              </div>
            </div>

            {/* Descripción */}
            <div className="border-t border-gray-200 pt-6">
              <ViewField 
                label="Descripción" 
                value={company.description} 
                icon={<Building2 className="w-4 h-4 text-gray-500" />}
              />
            </div>
          </div>
        ) : (
          // Modo edición con Formik
          <div className={sectionClass}>
            <Formik
              initialValues={company}
              validationSchema={validationSchema}
              onSubmit={handleSaveChanges}
            >
              {({ values, errors, touched, dirty, isValid, isSubmitting, setFieldValue, resetForm, handleBlur, setFieldTouched, setFieldError }) => {
                  return (
                    <Form className="w-full">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <EditField 
                          label="Razón social" 
                          name="business_name"  
                          helperText="Nombre registrado de la empresa"
                          icon={getFieldIcon('business_name')}
                        />
                        <EditField 
                          label="Nombre de fantasía" 
                          name="trade_name" 
                          helperText="Nombre comercial o de marca"
                          icon={getFieldIcon('trade_name')}
                        />
                        <EditField 
                          label="RUT" 
                          name="rut" 
                          helperText="Formato: 12.345.678-9"
                          type="text"
                          disabled
                          icon={<Building2 className="w-4 h-4 text-gray-500" />}
                        />
                        <EditSelectField
                          label="Industria"
                          name="industry"
                          helperText="Industria a la que pertenece la empresa"
                          options={industries}
                          icon={<Users className="w-4 h-4 text-indigo-500" />}
                        />
                        <EditField 
                          label="Correo de contacto" 
                          name="contact_email" 
                          type="email" 
                          helperText="Correo electrónico principal"
                          icon={getFieldIcon('contact_email')}
                        />
                        <EditField
                          label="Numero fijo"
                          name="landline_phone"
                          helperText="Número de teléfono fijo"
                          icon={getFieldIcon('landline_phone')}
                        />
                        <EditField
                          label="Numero movil"
                          name="mobile_phone"
                          helperText="Número de teléfono móvil"
                          icon={getFieldIcon('mobile_phone')}
                        />
                        <EditField 
                          label="Dirección" 
                          name="address" 
                          icon={getFieldIcon('address')}
                        />
                        
                        <div className="md:col-span-2">
                          <RegionProvinceDistrictFields 
                            regions={regionsData?.Regions || []}
                            styles={regionStyles}
                            helperTexts={{
                              region: "Seleccione la región",
                              province: "Seleccione la provincia",
                              district: "Seleccione la comuna"
                            }}
                          />
                        </div>

                        <EditField 
                          label="Latitud" 
                          name="latitude" 
                          type="number"
                          helperText="Decimal, ej: -33.456"
                          icon={getFieldIcon('latitude')}
                        />
                        <EditField 
                          label="Longitud" 
                          name="longitude" 
                          type="number"
                          helperText="Decimal, ej: -70.648"
                          icon={getFieldIcon('longitude')}
                        />

                        <EditSelectField 
                          label="Periodicidad" 
                          name="periodicity" 
                          options={periodicities}
                          helperText="Frecuencia de pago de cuota"
                          icon={<Calendar className="w-4 h-4 text-purple-500" />}
                          disabled={true}
                        />
                        
                        <EditField 
                          label="Monto cuota" 
                          name="amount" 
                          type="text" 
                          helperText="En UF"
                          icon={getFieldIcon('amount')}
                        />
                        
                        <EditSelectField 
                          label="Tamaño empresa" 
                          name="company_size" 
                          options={companySizes}
                          helperText="Clasificación según número de empleados"
                          icon={getFieldIcon('company_size')}
                        />
                        
                        <EditField 
                          label="Fecha de ingreso" 
                          name="entry_date" 
                          helperText="Formato: DD-MM-YYYY"
                          icon={getFieldIcon('entry_date')}
                        />
                        <EditSelectField 
                          label="Estado" 
                          name="status" 
                          options={modifiedCompanyStatus}
                          helperText="Estado actual de la empresa"
                          icon={<Award className="w-4 h-4 text-yellow-500" />}
                          disabled={isMerged}
                        />
                        <EditSelectField
                          label="Rol"
                          name="role"
                          options={[
                            { value: "admin", label: "Administrador" },
                            { value: "socio", label: "Socio" },
                          ]}
                          helperText="Rol asignado al usuario"
                          icon={<Users className="w-4 h-4 text-pink-500" />}
                        />
                        <EditField
                          label="Web"
                          name="web_page"
                          helperText="Página web de la empresa"
                          icon={getFieldIcon('web_page')}
                        />
                      </div>

                      <div className="border-t border-gray-200 pt-8 mt-8">
                        <EditTextareaField
                          label="Descripción"
                          name="description"
                          helperText="Detalle de la actividad de la empresa"
                        />
                      </div>

                      <div className="flex flex-col lg:flex-row gap-8 items-start w-full mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl">
                        {/* Subida de imagen */}
                        <div className="w-full lg:w-1/2">
                          <div className="mb-4">
                            <h3 className="text-lg font-semibold text-gray-700 mb-2">Logo de la empresa</h3>
                            <ImageUploaderWithCrop
                              aspectRatio={1}
                              onCropped={(blob) => {
                                const url = URL.createObjectURL(blob);
                                setFieldValue("logo", blob);
                                setImagePreview(url);
                                setFieldTouched("logo", true, false);
                              }}
                              setFieldTouched={setFieldTouched}
                              setFieldError={setFieldError}
                              maxFileSize={2097152}
                              fieldName="logo"
                              values={values}
                              setFieldValue={setFieldValue}
                            />
                          </div>
                          <p className={helperTextClass}>Formatos: JPG, PNG. Tamaño máximo: 2MB</p>
                          {touched.logo && errors.logo && (
                            <p className="text-red-500 text-sm mt-1">{errors.logo}</p>
                          )}
                        </div>

                        {/* Vista previa */}
                        <div className="flex flex-col items-center">
                          <div className="w-48 h-48 rounded-3xl overflow-hidden border-4 border-white shadow-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                            {imagePreview || company.logo ? (
                              <img
                                src={imagePreview || company.logo}
                                alt="Vista previa"
                                className="object-cover w-full h-full"
                              />
                            ) : (
                              <div className="text-center">
                                <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                                <p className="text-gray-400 text-sm px-4">Vista previa de la imagen</p>
                              </div>
                            )}
                          </div>
                          <p className="text-sm font-medium text-gray-600 mt-4">Vista previa</p>
                        </div>
                      </div>

                      {/* Botones de acción */}
                      {isEditing && (
                        <div className="flex justify-end mt-10 space-x-4">
                          <button
                            type="button"
                            onClick={() => handleClose()}
                            className="flex items-center px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 font-semibold shadow-lg"
                          >
                            <X className="w-5 h-5 mr-2" />
                            Cancelar
                          </button>

                          <button
                            type="submit"
                            disabled={!isValid || !dirty || isSubmitting}
                            className={`flex items-center px-8 py-4 rounded-xl shadow-lg transition-all duration-300 font-semibold ${
                              !isValid || !dirty || isSubmitting
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 transform hover:scale-105"
                            }`}
                          >
                            <Save className="w-5 h-5 mr-2" />
                            Guardar cambios
                          </button>
                        </div>
                      )}

                      {/* Errores de validación */}
                      {!isValid && Object.keys(errors).length > 0 && (
                        <div className="mt-8 p-6 bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 rounded-2xl">
                          <h4 className="font-bold text-red-700 mb-4 text-lg">Faltan campos o hay errores:</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {Object.entries(errors).map(([field, errorMsg]) => (
                              <div key={field} className="flex items-center gap-2 text-red-600 text-sm bg-white p-3 rounded-lg">
                                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                {errorMsg}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </Form>
                  );
              }}
            </Formik>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberDetailsView;
